// src/features/distributionBoard/modalContent/DistributionBoardModalContent.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector }    from 'react-redux';
import { updateNodeAttributes }        from '../../../../redux/reducers/global/node/nodeSlice';
import './DistributionBoardModalContent.css';
import { closeModal } from '../../../../redux/reducers/global/modal/modalSlice';
import { buildDistributionBoard, setDistributionBoardConfig } from '../../../../redux/reducers/global/distributionBoard/distributionBoardSlice';
import { updateDiagram } from '../../../../redux/reducers/global/diagram/diagramSlice';

const HEADER_GROUPS = [
  {
    key: 'protection',
    label: 'Protección',
    columns: ['Marca', 'Modelo', 'In'],
  },
  {
    key: 'cable',
    label: 'Cable',
    columns: ['Calibre', 'Longitud'],
  },
];

const MIN_COLUMN_WIDTH = 96;
const DEFAULT_COLUMN_WIDTH = 140;
const DEFAULT_DESCRIPTION_WIDTH = 240;
const DEFAULT_CIRCUIT_WIDTH = 110;

const getDefaultColumnWidth = (columnName, columnIndex) => {
  if (columnIndex === 0 || columnName === 'Circuito') {
    return DEFAULT_CIRCUIT_WIDTH;
  }
  if (columnName === 'Descripción de Carga') {
    return DEFAULT_DESCRIPTION_WIDTH;
  }
  return DEFAULT_COLUMN_WIDTH;
};

const normalizeColumnWidths = (headers = [], widths = []) => (
  headers.map((columnName, index) => {
    const candidate = Number(widths[index]);
    if (Number.isFinite(candidate) && candidate >= MIN_COLUMN_WIDTH) {
      return candidate;
    }
    return getDefaultColumnWidth(columnName, index);
  })
);

const getColumnClassName = (columnName, columnIndex) => {
  if (columnIndex === 0 || columnName === 'Circuito') {
    return 'distribution-col distribution-col--circuit';
  }
  if (columnName === 'Descripción de Carga') {
    return 'distribution-col distribution-col--description';
  }
  if (columnName === 'Marca' || columnName === 'Modelo') {
    return 'distribution-col distribution-col--medium';
  }
  if (columnName === 'Calibre' || columnName === 'Longitud') {
    return 'distribution-col distribution-col--medium';
  }
  return 'distribution-col distribution-col--default';
};

const renderHeaderLabel = (columnName, extraClassName = '', onChange = null, onInput = null, inputRef = null) => {
  const className = `header-input${extraClassName ? ` ${extraClassName}` : ''}`;
  return (
    <textarea
      className={`${className}${columnName === 'Descripción de Carga' ? ' header-input--multiline' : ''}`}
      value={columnName}
      onChange={onChange}
      onInput={onInput}
      ref={inputRef}
      rows={columnName === 'Descripción de Carga' ? 2 : 1}
    />
  );
};

const cloneDistributionSnapshot = (headers = [], rows = [], columnWidths = []) => ({
  headers: Array.isArray(headers) ? [...headers] : [],
  rows: Array.isArray(rows) ? rows.map((row) => ({ ...row })) : [],
  columnWidths: Array.isArray(columnWidths) ? [...columnWidths] : [],
});

const buildTableTarget = (rowIndex, colIndex) => ({
  rowIndex: Number.isInteger(rowIndex) ? rowIndex : null,
  colIndex: Number.isInteger(colIndex) ? colIndex : null,
});

const autoSizeTextareaElement = (element) => {
  if (!element) return;
  element.style.height = 'auto';
  element.style.height = `${Math.max(element.scrollHeight, 40)}px`;
};

const DistributionBoardModalContent = ({ onClose }) => {
  const dispatch = useDispatch();

  // Load whatever headers/rows (or default 3x3) into our slice:
  useEffect(() => {
    dispatch(buildDistributionBoard());
  }, [dispatch]);

  // Subscribe to slice
  const { headerData, rowData, nodeId } = useSelector(s => s.distributionBoard);
  const node = useSelector(s => s.node.nodes.find(n => n.id === nodeId));
  const diagramLoading = useSelector(s => !!s.diagram?.diagramLoading);

  // Local copies for immediate editing
  const [headers, setHeaders] = useState([]);
  const [rows, setRows]       = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [columnUndoStack, setColumnUndoStack] = useState([]);
  const [tableClipboard, setTableClipboard] = useState('');
  const [activeTarget, setActiveTarget] = useState(null);
  const [columnWidths, setColumnWidths] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const resizeStateRef = useRef(null);
  const showSavingOverlay = isSaving || diagramLoading;

  // Mirror Redux → local on every change
  useEffect(() => {
    setHeaders(headerData);
    setRows(rowData);
    setColumnUndoStack([]);
    setTableClipboard('');
    setActiveTarget(null);
    const widthValue = Array.isArray(node?.data?.attributes?.columnWidths?.value)
      ? node.data.attributes.columnWidths.value
      : [];
    setColumnWidths(normalizeColumnWidths(headerData, widthValue));
  }, [headerData, rowData]);

  // Handlers
  const handleHeaderChange = (i, val) => {
    if (!Number.isInteger(i) || i < 0 || i >= headers.length) {
      return;
    }
    const previousHeader = headers[i];
    const nextHeader = val;
    if (previousHeader === nextHeader) {
      return;
    }

    const nextHeaders = [...headers];
    nextHeaders[i] = nextHeader;
    setHeaders(nextHeaders);

    setRows(rows.map((row) => {
      if (!Object.prototype.hasOwnProperty.call(row, previousHeader)) {
        return row;
      }
      const nextRow = { ...row };
      nextRow[nextHeader] = nextRow[previousHeader];
      if (nextHeader !== previousHeader) {
        delete nextRow[previousHeader];
      }
      return nextRow;
    }));
  };

  const handleCellChange = (r, col) => e => {
    const newRows = rows.map((row, idx) =>
      idx === r ? { ...row, [col]: e.target.value } : row
    );
    setRows(newRows);
  };

  const autoSizeOnChange = (event) => {
    autoSizeTextareaElement(event.target);
  };

  const registerAutoSizeRef = useCallback((element) => {
    autoSizeTextareaElement(element);
  }, []);

  const setTargetValue = useCallback((target, value) => {
    if (!target || !Number.isInteger(target.colIndex) || target.colIndex < 0 || target.colIndex >= headers.length) {
      return false;
    }
    const nextValue = value ?? '';
    if (Number.isInteger(target.rowIndex) && target.rowIndex >= 0 && target.rowIndex < rows.length) {
      const columnName = headers[target.colIndex];
      setRows(rows.map((row, idx) => (
        idx === target.rowIndex ? { ...row, [columnName]: nextValue } : row
      )));
      return true;
    }
    const previousHeader = headers[target.colIndex];
    const nextHeaders = headers.map((header, idx) => (idx === target.colIndex ? nextValue : header));
    setHeaders(nextHeaders);
    setRows(rows.map((row) => {
      if (!Object.prototype.hasOwnProperty.call(row, previousHeader)) {
        return row;
      }
      const nextRow = { ...row };
      nextRow[nextValue] = nextRow[previousHeader];
      if (nextValue !== previousHeader) {
        delete nextRow[previousHeader];
      }
      return nextRow;
    }));
    return true;
  }, [headers, rows]);

  const getTargetValue = useCallback((target) => {
    if (!target || !Number.isInteger(target.colIndex) || target.colIndex < 0 || target.colIndex >= headers.length) {
      return '';
    }
    if (Number.isInteger(target.rowIndex) && target.rowIndex >= 0 && target.rowIndex < rows.length) {
      const columnName = headers[target.colIndex];
      return rows[target.rowIndex]?.[columnName] ?? '';
    }
    return headers[target.colIndex] ?? '';
  }, [headers, rows]);

  const resolveCurrentTarget = useCallback(() => {
    if (contextMenu) {
      return buildTableTarget(contextMenu.rowIndex, contextMenu.colIndex);
    }
    return activeTarget;
  }, [activeTarget, contextMenu]);

  const copyCurrentTarget = useCallback(async () => {
    const target = resolveCurrentTarget();
    const value = getTargetValue(target);
    const text = typeof value === 'string' ? value : String(value ?? '');
    setTableClipboard(text);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      }
    } catch {}
    return text;
  }, [getTargetValue, resolveCurrentTarget]);

  const pasteIntoCurrentTarget = useCallback(async () => {
    const target = resolveCurrentTarget();
    if (!target) {
      return false;
    }
    let nextValue = tableClipboard;
    try {
      if (navigator?.clipboard?.readText) {
        const clipboardText = await navigator.clipboard.readText();
        if (typeof clipboardText === 'string') {
          nextValue = clipboardText;
        }
      }
    } catch {}
    if (typeof nextValue !== 'string') {
      return false;
    }
    const applied = setTargetValue(target, nextValue);
    if (applied) {
      setTableClipboard(nextValue);
    }
    return applied;
  }, [resolveCurrentTarget, setTargetValue, tableClipboard]);

  const pushColumnUndoSnapshot = useCallback(() => {
    setColumnUndoStack((prev) => [...prev, cloneDistributionSnapshot(headers, rows, columnWidths)]);
  }, [headers, rows, columnWidths]);

  const handleAddColumn = afterIdx => {
    const promptValue = prompt('Nombre de la nueva columna:');
    if (promptValue === null) {
      return;
    }
    let label = promptValue;
    if (!label.trim()) {
      // fallback unique
      let i = 1;
      do { label = `Col${i++}`; }
      while (headers.includes(label));
    }
    pushColumnUndoSnapshot();
    const pos = afterIdx >= 0 ? afterIdx + 1 : 0;
    setHeaders([
      ...headers.slice(0, pos),
      label,
      ...headers.slice(pos)
    ]);
    setRows(rows.map(r => ({ ...r, [label]: '' })));
    setColumnWidths((prev) => {
      const next = [...prev];
      next.splice(pos, 0, getDefaultColumnWidth(label, pos));
      return normalizeColumnWidths([...headers.slice(0, pos), label, ...headers.slice(pos)], next);
    });
  };

  const handleDeleteColumn = (columnIndex) => {
    if (!Number.isInteger(columnIndex) || columnIndex <= 0 || columnIndex >= headers.length) {
      return;
    }
    pushColumnUndoSnapshot();
    const columnName = headers[columnIndex];
    const nextHeaders = headers.filter((_, idx) => idx !== columnIndex);
    const nextRows = rows.map((row) => {
      const nextRow = { ...row };
      delete nextRow[columnName];
      return nextRow;
    });
    setHeaders(nextHeaders);
    setRows(nextRows);
    setColumnWidths((prev) => normalizeColumnWidths(nextHeaders, prev.filter((_, idx) => idx !== columnIndex)));
  };

  const handleAddRow = afterIdx => {
    const pos = afterIdx >= 0 ? afterIdx + 1 : 0;
    const newRow = headers.reduce((o,h) => ({ ...o, [h]: '' }), {});
    setRows([
      ...rows.slice(0, pos),
      newRow,
      ...rows.slice(pos)
    ]);
  };

  const handleDeleteRow = (rowIndex) => {
    if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex >= rows.length) {
      return;
    }
    setRows(rows.filter((_, idx) => idx !== rowIndex));
  };

  const handleSave = async () => {
    if (!node || showSavingOverlay) {
      return;
    }
    setIsSaving(true);
    const oldHdr = node.data?.attributes?.headerData || {};
    const oldRow = node.data?.attributes?.rowData || {};
    const oldWidths = node.data?.attributes?.columnWidths || {};

    dispatch(updateNodeAttributes({
      nodeId,
      newAttributes: {
        headerData: { ...oldHdr, value: headers, userModified: true, systemDefined: true },
        rowData:    { ...oldRow, value: rows,    userModified: true, systemDefined: true },
        columnWidths: { ...oldWidths, value: columnWidths, userModified: true, systemDefined: true }
      }
    }));
    dispatch(setDistributionBoardConfig({
      nodeId,
      headers: headers.slice(),
      rows: rows.map((row) => ({ ...row })),
    }));
    try {
      await dispatch(updateDiagram()).unwrap();
      dispatch(closeModal());
      setHeaders(headers.slice());
      setRows(rows.map((row) => ({ ...row })));
      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (error) {
      console.error('No se pudo guardar el diagrama después de actualizar el panel de distribución:', error);
      setIsSaving(false);
    }
  };

  const restoreNodeConfig = () => {
    if (!node) {
      return;
    }
    const nodeHeaders = Array.isArray(node.data?.attributes?.headerData?.value)
      ? [...node.data.attributes.headerData.value]
      : [];
    const nodeRows = Array.isArray(node.data?.attributes?.rowData?.value)
      ? node.data.attributes.rowData.value.map((row) => ({ ...row }))
      : [];
    const nodeColumnWidths = Array.isArray(node.data?.attributes?.columnWidths?.value)
      ? node.data.attributes.columnWidths.value
      : [];

    setHeaders(nodeHeaders);
    setRows(nodeRows);
    setColumnWidths(normalizeColumnWidths(nodeHeaders, nodeColumnWidths));
    dispatch(setDistributionBoardConfig({
      nodeId,
      headers: nodeHeaders,
      rows: nodeRows,
    }));
  };

  const handleCancel = () => {
    if (showSavingOverlay) {
      return;
    }
    setContextMenu(null);
    restoreNodeConfig();
    dispatch(closeModal());
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const closeContextMenu = () => setContextMenu(null);

  const handleColumnResizeStart = useCallback((event, columnIndex) => {
    event.preventDefault();
    event.stopPropagation();
    resizeStateRef.current = {
      columnIndex,
      startX: event.clientX,
      startWidth: columnWidths[columnIndex] ?? getDefaultColumnWidth(headers[columnIndex], columnIndex),
    };
  }, [columnWidths, headers]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const activeResize = resizeStateRef.current;
      if (!activeResize) {
        return;
      }
      const deltaX = event.clientX - activeResize.startX;
      const nextWidth = Math.max(MIN_COLUMN_WIDTH, Math.round(activeResize.startWidth + deltaX));
      setColumnWidths((prev) => prev.map((width, index) => (
        index === activeResize.columnIndex ? nextWidth : width
      )));
    };

    const handlePointerUp = () => {
      resizeStateRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  const handleUndoColumnChange = useCallback(() => {
    let restored = false;
    setColumnUndoStack((prev) => {
      if (!prev.length) {
        return prev;
      }
      const snapshot = prev[prev.length - 1];
      restored = true;
      setHeaders(snapshot.headers);
      setRows(snapshot.rows);
      setColumnWidths(normalizeColumnWidths(snapshot.headers, snapshot.columnWidths));
      return prev.slice(0, -1);
    });
    if (restored) {
      setContextMenu(null);
    }
  }, []);

  useEffect(() => {
    if (!contextMenu) return undefined;

    const handlePointerDown = (event) => {
      if (event.target?.closest?.('.distribution-context-menu')) {
        return;
      }
      setContextMenu(null);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setContextMenu(null);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [contextMenu]);

  useEffect(() => {
    const handleUndoShortcut = (event) => {
      const key = typeof event.key === 'string' ? event.key.toLowerCase() : '';
      if ((event.ctrlKey || event.metaKey) && !event.altKey && key === 'z') {
        if (columnUndoStack.length === 0) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        handleUndoColumnChange();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && !event.altKey && key === 'c') {
        if (!resolveCurrentTarget()) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        void copyCurrentTarget();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && !event.altKey && key === 'v') {
        if (!resolveCurrentTarget()) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        void pasteIntoCurrentTarget();
      }
    };

    window.addEventListener('keydown', handleUndoShortcut);
    return () => {
      window.removeEventListener('keydown', handleUndoShortcut);
    };
  }, [columnUndoStack.length, copyCurrentTarget, handleUndoColumnChange, pasteIntoCurrentTarget, resolveCurrentTarget]);

  const openContextMenu = (event, payload) => {
    event.preventDefault();
    event.stopPropagation();
    if (showSavingOverlay) {
      return;
    }
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      ...payload,
    });
  };

  const contextActions = useMemo(() => {
    if (!contextMenu) return [];
    const actions = [
      {
        key: 'copy-target',
        label: 'Copiar',
        disabled: !resolveCurrentTarget(),
        onClick: () => {
          void copyCurrentTarget();
        },
      },
      {
        key: 'paste-target',
        label: 'Pegar',
        disabled: !resolveCurrentTarget(),
        onClick: () => {
          void pasteIntoCurrentTarget();
        },
      },
      {
        key: 'undo-column-change',
        label: 'Deshacer',
        disabled: columnUndoStack.length === 0,
        onClick: handleUndoColumnChange,
      },
      {
        key: 'edit-separator',
        type: 'separator',
      },
      {
        key: 'add-column-right',
        label: 'Agregar una columna a la derecha',
        onClick: () => handleAddColumn(contextMenu.colIndex),
      },
      {
        key: 'delete-column',
        label: 'Borrar columna',
        disabled: contextMenu.colIndex === 0,
        onClick: () => handleDeleteColumn(contextMenu.colIndex),
      },
      {
        key: 'add-row-below',
        label: 'Agregar una fila debajo',
        onClick: () => handleAddRow(contextMenu.actionRowIndex ?? rows.length - 1),
      },
      {
        key: 'delete-row',
        label: 'Eliminar la fila',
        disabled: !Number.isInteger(contextMenu.actionRowIndex),
        onClick: () => handleDeleteRow(contextMenu.actionRowIndex),
      },
    ];
    return actions;
  }, [columnUndoStack.length, contextMenu, copyCurrentTarget, handleUndoColumnChange, pasteIntoCurrentTarget, resolveCurrentTarget, rows.length]);

  const renderHeaderRows = () => {
    const hasGroupedLayout = headers.includes('Circuito')
      && headers.includes('Descripción de Carga')
      && HEADER_GROUPS.every((group) => group.columns.every((column) => headers.includes(column)));

    if (!hasGroupedLayout) {
      return (
        <thead>
          <tr>
            {headers.map((col, i) => (
              <th
                key={`header-${i}`}
                className={`header-cell ${getColumnClassName(col, i)}${i === 0 ? ' first-column-header' : ''}`}
                style={{ width: columnWidths[i], minWidth: columnWidths[i] }}
                onContextMenu={(event) => openContextMenu(event, { colIndex: i, rowIndex: null, actionRowIndex: rows.length ? 0 : null })}
                onPointerDown={() => setActiveTarget(buildTableTarget(null, i))}
              >
                {renderHeaderLabel(
                  col,
                  '',
                  (event) => {
                    handleHeaderChange(i, event.target.value);
                    autoSizeOnChange(event);
                  },
                  autoSizeOnChange,
                  registerAutoSizeRef,
                )}
                <div
                  className="distribution-column-resizer"
                  onPointerDown={(event) => handleColumnResizeStart(event, i)}
                />
              </th>
            ))}
          </tr>
        </thead>
      );
    }

    const groupedColumns = new Set(HEADER_GROUPS.flatMap((group) => group.columns));
    const groupByColumn = new Map(
      HEADER_GROUPS.flatMap((group) => group.columns.map((column) => [column, group]))
    );
    const renderedGroups = new Set();
    const topRowCells = [];

    headers.forEach((col, index) => {
      const group = groupByColumn.get(col);
      if (group) {
        if (renderedGroups.has(group.key)) {
          return;
        }
        renderedGroups.add(group.key);
        topRowCells.push(
          <th key={`group-${group.key}-${index}`} className="header-cell group-header-cell" colSpan={group.columns.length}>
            <div className="header-group-label">{group.label}</div>
          </th>
        );
        return;
      }

      topRowCells.push(
        <th
          key={`${col}-${index}`}
          className={`header-cell${index === 0 ? ' first-column-header' : ''} group-header-cell ${getColumnClassName(col, index)}`}
          rowSpan={2}
          style={{ width: columnWidths[index], minWidth: columnWidths[index] }}
          onContextMenu={(event) => openContextMenu(event, { colIndex: index, rowIndex: null, actionRowIndex: rows.length ? 0 : null })}
          onPointerDown={() => setActiveTarget(buildTableTarget(null, index))}
        >
          {renderHeaderLabel(
            col,
            'header-input--group',
            (event) => {
              handleHeaderChange(index, event.target.value);
              autoSizeOnChange(event);
            },
            autoSizeOnChange,
            registerAutoSizeRef,
          )}
          <div
            className="distribution-column-resizer"
            onPointerDown={(event) => handleColumnResizeStart(event, index)}
          />
        </th>
      );
    });

    return (
      <thead>
        <tr>{topRowCells}</tr>
        <tr>
          {headers
            .filter((col) => groupedColumns.has(col))
            .map((col) => {
              const index = headers.indexOf(col);
              return (
                <th
                  key={`subheader-${index}`}
                  className={`header-cell subheader-cell ${getColumnClassName(col, index)}`}
                  style={{ width: columnWidths[index], minWidth: columnWidths[index] }}
                  onContextMenu={(event) => openContextMenu(event, { colIndex: index, rowIndex: null, actionRowIndex: rows.length ? 0 : null })}
                  onPointerDown={() => setActiveTarget(buildTableTarget(null, index))}
                >
                  {renderHeaderLabel(
                    col,
                    '',
                    (event) => {
                      handleHeaderChange(index, event.target.value);
                      autoSizeOnChange(event);
                    },
                    autoSizeOnChange,
                    registerAutoSizeRef,
                  )}
                  <div
                    className="distribution-column-resizer"
                    onPointerDown={(event) => handleColumnResizeStart(event, index)}
                  />
                </th>
              );
            })}
        </tr>
      </thead>
    );
  };

  return (
    <div className="distribution-modal">
      {showSavingOverlay ? (
        <div className="distribution-modal__saving-overlay" role="status" aria-live="polite">
          <div className="distribution-modal__saving-spinner" aria-hidden="true" />
          <div className="distribution-modal__saving-text">Guardando panel...</div>
        </div>
      ) : null}
      <h2>Configurar Panel de Distribución</h2>
      <div className="distribution-table-container">
        <div className="distribution-table-name">Centros de carga</div>
        <table className="distribution-table">
          <colgroup>
            {headers.map((col, index) => (
              <col
                key={`col-${index}`}
                style={{ width: columnWidths[index], minWidth: columnWidths[index] }}
              />
            ))}
          </colgroup>
          {renderHeaderRows()}
          <tbody>
            {rows.map((rowObj, rIdx) => (
              <tr key={rIdx} className={rIdx%2===0 ? 'even' : 'odd'}>
                {headers.map((col,cIdx) => (
                  <td
                    key={cIdx}
                    className={`${getColumnClassName(col, cIdx)}${cIdx === 0 ? ' first-column-cell' : ''}`}
                    style={{ width: columnWidths[cIdx], minWidth: columnWidths[cIdx] }}
                    onContextMenu={(event) => openContextMenu(event, { rowIndex: rIdx, actionRowIndex: rIdx, colIndex: cIdx })}
                  >
                    <textarea
                      className={`cell-input${cIdx === 0 ? ' first-column-input' : ''}`}
                      value={rowObj[col]||''}
                      onChange={handleCellChange(rIdx, col)}
                      onInput={autoSizeOnChange}
                      onFocus={() => setActiveTarget(buildTableTarget(rIdx, cIdx))}
                      ref={registerAutoSizeRef}
                      rows={1}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="modal-actions">
        <div className="modal-actions__hint">Click derecho para editar tabla</div>
        <button className="modal-action-btn modal-action-btn--secondary" onClick={handleCancel} disabled={showSavingOverlay}>Cancelar</button>
        <button className="modal-action-btn modal-action-btn--primary" onClick={handleSave} disabled={showSavingOverlay}>Guardar</button>
      </div>
      {contextMenu ? (
        <div
          className="distribution-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onPointerDown={(event) => event.stopPropagation()}
        >
          {contextActions.map((action) => (
            action.type === 'separator' ? (
              <div
                key={action.key}
                className="distribution-context-menu__separator"
                role="separator"
              />
            ) : (
              <button
                key={action.key}
                type="button"
                className="distribution-context-menu__item"
                disabled={showSavingOverlay || action.disabled}
                onClick={() => {
                  if (showSavingOverlay || action.disabled) return;
                  action.onClick();
                  closeContextMenu();
                }}
              >
                {action.label}
              </button>
            )
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default DistributionBoardModalContent;
